import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList,
  PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// Gestão do BPO RH — a visão do GESTOR sobre a operação inteira, uma competência
// por vez: produção por serviço, evolução, operadoras, clientes, atrasos
// (interno x externo) e atendimento. Mesma linguagem visual do Painel do
// Operador (po-*), com abas como a Gestão Contábil.
const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
// paleta categórica validada (dataviz): azul, verde, âmbar, vermelho
const COR = { azul: '#0a6ea1', verde: '#2e8b57', ambar: '#d4a017', vermelho: '#b3261e', navy: '#00205b', cinza: '#8a94a6' };
const SERIES = { admissoes: COR.azul, rescisoes: COR.vermelho, ferias: COR.verde, avisos: COR.ambar };
const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const num = (v, casas = 0) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const pct = (v) => (v == null ? '—' : `${num(v, 1)}%`);
// horas decimais -> "19h54min" (0,6 -> 0h36min); zero vira "—"
const hm = (v) => {
  if (v == null) return '—';
  const total = Math.round(Number(v) * 60);
  if (!total) return '0min';
  const h = Math.floor(total / 60), m = total % 60;
  return h ? `${h}h${String(m).padStart(2, '0')}min` : `${m}min`;
};

const TooltipCard = ({ active, payload, label, unidade }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e6e8eb', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,32,91,.12)', padding: '10px 12px', fontSize: 13, minWidth: 160 }}>
      <div style={{ fontWeight: 700, color: '#212529', marginBottom: 6 }}>{label}</div>
      {payload.filter((p) => p.value).map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#495057', padding: '2px 0' }}>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: p.color || p.fill, marginRight: 7 }}></span>{p.name}</span>
          <b style={{ color: '#212529', fontVariantNumeric: 'tabular-nums' }}>{unidade === 'h' ? hm(p.value) : num(p.value)}</b>
        </div>
      ))}
    </div>
  );
};

const Legenda = ({ itens }) => (
  <div style={{ display: 'flex', gap: 18, justifyContent: 'center', flexWrap: 'wrap', fontSize: 12.5, color: '#495057', paddingTop: 8 }}>
    {itens.map(([rot, cor]) => (
      <span key={rot} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 10, background: cor, display: 'inline-block' }}></span>{rot}
      </span>
    ))}
  </div>
);

const Bloco = ({ titulo, sub, icone, children, count, countTipo, acao }) => (
  <section className="po-card po-enter">
    <header className="po-card-head">
      <h3><i className={`fas ${icone}`} aria-hidden="true"></i>{titulo}</h3>
      {sub && <span className="po-sub">{sub}</span>}
      {count !== undefined && <span className={`badge ${countTipo === 'bad' ? 'bg-danger' : countTipo === 'warn' ? 'bg-warning text-dark' : 'bg-light text-dark'} ms-1`}>{count}</span>}
      {acao && <span className="ms-auto-wrap">{acao}</span>}
    </header>
    <div className="po-card-body pad">{children}</div>
  </section>
);

// tabela compacta e ordenável (clique no cabeçalho)
const Tabela = ({ colunas, linhas, chave, ordemInicial, vazio }) => {
  const [ordem, setOrdem] = useState(ordemInicial || { col: colunas[0].chave, dir: 'asc' });
  const ordenadas = useMemo(() => {
    const col = colunas.find((c) => c.chave === ordem.col);
    if (!col) return linhas;
    const val = col.valor || ((l) => l[col.chave]);
    return [...linhas].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const r = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), 'pt-BR');
      return ordem.dir === 'asc' ? r : -r;
    });
  }, [linhas, colunas, ordem]);
  return (
    <div className="po-scroll">
      <table className="table table-hover po-table align-middle">
        <thead>
          <tr>
            {colunas.map((c) => (
              <th key={c.chave} style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...(c.estilo || {}) }}
                className={c.classe === 'num' ? 'text-end' : ''}
                onClick={() => setOrdem((o) => ({ col: c.chave, dir: o.col === c.chave && o.dir === 'asc' ? 'desc' : 'asc' }))}>
                {c.rotulo}{ordem.col === c.chave && <i className={`fas fa-caret-${ordem.dir === 'asc' ? 'up' : 'down'} ms-1 text-muted`}></i>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ordenadas.map((l) => (
            <tr key={chave(l)}>
              {colunas.map((c) => (
                <td key={c.chave} className={c.classe === 'num' ? 'text-end tabular' : ''} style={c.classe === 'num' ? { fontVariantNumeric: 'tabular-nums' } : {}}>
                  {c.render ? c.render(l) : (c.valor ? c.valor(l) : l[c.chave])}
                </td>
              ))}
            </tr>
          ))}
          {!ordenadas.length && <tr><td colSpan={colunas.length} className="po-vazio text-center text-muted py-4">{vazio || 'Nada neste período.'}</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const GestaoBpoRh = () => {
  const [mes, setMes] = useState(mesAtual());
  // janela da evolução (de/até, em AAAA-MM) — independente da competência dos KPIs
  const somaMeses = (ym, n) => { const [a, m] = ym.split('-').map(Number); const d = new Date(a, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
  const [evoDe, setEvoDe] = useState(somaMeses(mesAtual(), -5));
  const [evoAte, setEvoAte] = useState(mesAtual());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [negado, setNegado] = useState(false);
  const [aba, setAba] = useState('geral');
  const [cliEvo, setCliEvo] = useState('todas');   // empresa do gráfico de evolução (aba Clientes)

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    axios.get(`${API}/api/bporh/gestao/`, { params: { mes, de: evoDe, ate: evoAte, _: Date.now() }, headers: headers() })
      .then((r) => setDados(r.data))
      .catch((e) => {
        if (e.response?.status === 403) setNegado(true);
        else setErro(e.response?.data?.mensagem || 'Não foi possível carregar a gestão');
      })
      .finally(() => setCarregando(false));
  }, [mes, evoDe, evoAte]);
  useEffect(() => { carregar(); }, [carregar]);

  const k = dados?.kpis || {};
  const ops = dados?.operadoras || [];
  const cli = dados?.clientes || [];
  const atr = dados?.atrasos || { por_origem: {}, por_tipo: [], por_operador: [], lista: [] };
  const evolucao = dados?.evolucao || [];

  const kpi = (icone, rotulo, valor, sub, cor = 'navy') => (
    <div key={rotulo} className={`po-kpi c-${cor} ${!valor ? 'po-zero' : ''}`} style={{ cursor: 'default' }}>
      <div className="po-kpi-rotulo"><i className={`fas ${icone}`} aria-hidden="true"></i>{rotulo}</div>
      <div className="po-kpi-valor">{valor == null ? '—' : valor}</div>
      <div className="po-kpi-sub">{sub}</div>
    </div>
  );

  const abas = [
    ['geral', 'fa-gauge-high', 'Visão geral'], ['operadoras', 'fa-user-tie', 'Operadoras'],
    ['clientes', 'fa-building', 'Clientes'], ['atrasos', 'fa-triangle-exclamation', 'Atrasos', atr.total || 0],
    ['atendimento', 'fa-comments', 'Atendimento e tempo'],
  ];

  if (negado) {
    return (
      <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white text-center py-5">
        <i className="fas fa-lock fa-3x text-muted mb-3"></i>
        <h2 className="mb-2">Página restrita</h2>
        <p className="text-muted mb-0">A Gestão do BPO RH é da coordenação. Se você precisa desse acesso, fale com a administração do Saber.</p>
      </div></div>
    );
  }

  return (
    <div className="painel-op">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-chart-line header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Gestão{dados && <span className="text-muted"> · {dados.mes_rotulo}</span>}</h2>
          </div>
          <div className="gr-toolbar">
            <label className="gr-campo" title="Mês analisado nos indicadores e na produção">
              <i className="fas fa-calendar-days" aria-hidden="true"></i>Competência
              <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} aria-label="Competência" />
            </label>
            <Link to="/BpoRh/PainelOperador" className="gr-btn"><i className="fas fa-gauge-high" aria-hidden="true"></i>Painel do Operador</Link>
            <button type="button" className="gr-btn primaria" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2"><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}

        <div className="gr-tabs" role="tablist" aria-label="Seções da gestão">
          {abas.map(([id, icone, rot, cont]) => (
            <button key={id} type="button" role="tab" aria-selected={aba === id}
              className={`gr-tab ${aba === id ? 'ativa' : ''}`} onClick={() => setAba(id)}>
              <i className={`fas ${icone}`} aria-hidden="true"></i>{rot}
              {cont ? <span className="gr-cont">{cont}</span> : null}
            </button>
          ))}
        </div>

        {carregando && !dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Lendo a operação do mês...</div></div>
        ) : dados && (
          <>
            {/* ═══════════ VISÃO GERAL ═══════════ */}
            {aba === 'geral' && (
              <>
                <div className="po-kpis mb-3">
                  {kpi('fa-users', 'Colaboradores ativos', num(k.colaboradores_ativos), `${k.clientes} empresa(s) na carteira`, 'navy')}
                  {kpi('fa-arrow-right-to-bracket', 'Admitidos no mês', k.admitidos_convenia, 'entradas na Convenia', 'cyan')}
                  {kpi('fa-arrow-right-from-bracket', 'Desligados no mês', k.desligados_convenia, `turnover ${pct(k.turnover_pct)}`, k.turnover_pct > 5 ? 'bad' : 'cyan')}
                  {kpi('fa-triangle-exclamation', 'Atrasos justificados', k.atrasos_justificados, <><b>{k.atrasos_internos}</b> internos · <b>{k.atrasos_externos}</b> externos</>, k.atrasos_internos ? 'bad' : 'ok')}
                </div>

                <Bloco titulo="Evolução" sub={`entradas, saídas e férias iniciadas · ${dados.meses} mês(es)`} icone="fa-chart-area"
                  acao={(
                    <span className="d-inline-flex align-items-center gap-1 small" style={{ color: 'rgba(255,255,255,.85)' }}>
                      <i className="fas fa-calendar-days me-1"></i>de
                      <input type="month" className="form-control form-control-sm py-0" style={{ width: 150 }} value={evoDe} max={evoAte}
                        onChange={(e) => e.target.value && setEvoDe(e.target.value)} title="Primeiro mês do gráfico" />
                      até
                      <input type="month" className="form-control form-control-sm py-0" style={{ width: 150 }} value={evoAte} min={evoDe}
                        onChange={(e) => e.target.value && setEvoAte(e.target.value)} title="Último mês do gráfico" />
                      {[3, 6, 12].map((n) => (
                        <button key={n} type="button" className="btn btn-sm btn-light border py-0 px-2" style={{ fontSize: '.75rem' }}
                          onClick={() => { setEvoAte(mes); setEvoDe(somaMeses(mes, -(n - 1))); }} title={`Últimos ${n} meses até a competência`}>{n}m</button>
                      ))}
                    </span>
                  )}>
                  <ResponsiveContainer width="100%" height={380}>
                    <AreaChart data={evolucao} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
                      <defs>
                        {[['gAdm', COR.verde], ['gDes', COR.vermelho], ['gFer', COR.azul]].map(([id, cor]) => (
                          <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={cor} stopOpacity={0.28} />
                            <stop offset="100%" stopColor={cor} stopOpacity={0.02} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                      <XAxis dataKey="rotulo" tick={{ fontSize: 13, fill: '#495057' }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} width={36} />
                      <Tooltip content={<TooltipCard />} />
                      <Legend content={<Legenda itens={[['Admitidos', COR.verde], ['Desligados', COR.vermelho], ['Férias iniciadas', COR.azul]]} />} />
                      <Area type="monotone" dataKey="admitidos" name="Admitidos" stroke={COR.verde} fill="url(#gAdm)" strokeWidth={2.5}
                        dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }} />
                      <Area type="monotone" dataKey="desligados" name="Desligados" stroke={COR.vermelho} fill="url(#gDes)" strokeWidth={2.5}
                        dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }} />
                      <Area type="monotone" dataKey="ferias" name="Férias iniciadas" stroke={COR.azul} fill="url(#gFer)" strokeWidth={2.5}
                        dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Bloco>

                <div className="row g-3 mt-1">
                  <div className="col-xl-7">
                    <Bloco titulo="Serviços por mês" sub="admissões, rescisões, férias iniciadas e avisos/contratos" icone="fa-chart-column">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={evolucao} margin={{ top: 12, right: 12, left: 0, bottom: 0 }} barGap={2} barCategoryGap={18}>
                          <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                          <XAxis dataKey="rotulo" tick={{ fontSize: 12, fill: '#495057' }} axisLine={false} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} width={36} />
                          <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                          <Legend content={<Legenda itens={[['Admissões', SERIES.admissoes], ['Rescisões', SERIES.rescisoes], ['Férias iniciadas', SERIES.ferias], ['Avisos e contratos', SERIES.avisos]]} />} />
                          <Bar dataKey="admissoes" name="Admissões" fill={SERIES.admissoes} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="rescisoes" name="Rescisões" fill={SERIES.rescisoes} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="ferias" name="Férias iniciadas" fill={SERIES.ferias} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="avisos" name="Avisos e contratos" fill={SERIES.avisos} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Bloco>
                  </div>
                  <div className="col-xl-5">
                    <Bloco titulo="Atendimento" sub="mensagens do Slack por mês (cliente + equipe)" icone="fa-comments">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={evolucao} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} barCategoryGap={26}>
                          <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                          <XAxis dataKey="rotulo" tick={{ fontSize: 12, fill: '#495057' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} width={40} />
                          <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                          <Bar dataKey="mensagens" name="Mensagens" fill={COR.azul} radius={[6, 6, 0, 0]}>
                            <LabelList dataKey="mensagens" position="top" formatter={(v) => (v ? num(v) : '')} style={{ fontSize: 11, fill: '#212529', fontWeight: 700 }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Bloco>
                  </div>
                </div>

                <div className="mt-3">
                  <Bloco titulo="Cronômetro por cliente" sub={`horas registradas na área RH em ${dados.mes_rotulo} · quem liga o timer ao abrir a empresa`} icone="fa-stopwatch"
                    count={dados.tempo_por_empresa?.length ? hm(dados.tempo_por_empresa.reduce((s, x) => s + x.horas, 0)) : undefined}>
                    {dados.tempo_por_empresa?.length ? (
                      <ResponsiveContainer width="100%" height={Math.max(160, dados.tempo_por_empresa.length * 38 + 30)}>
                        <BarChart data={dados.tempo_por_empresa} layout="vertical" margin={{ top: 4, right: 64, left: 8, bottom: 0 }} barSize={18}>
                          <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                          <XAxis type="number" tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}h`} />
                          <YAxis type="category" dataKey="empresa" width={150} tick={{ fontSize: 13, fill: '#212529', fontWeight: 600 }} axisLine={false} tickLine={false} />
                          <Tooltip content={<TooltipCard unidade="h" />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                          <Bar dataKey="horas" name="Horas" fill={COR.ambar} radius={[0, 8, 8, 0]}>
                            <LabelList dataKey="horas" position="right" offset={8} formatter={(v) => hm(v)} style={{ fontSize: 13, fontWeight: 700, fill: '#212529' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div className="po-vazio text-center text-muted py-4">Nenhum tempo apontado na área RH neste mês.</div>}
                  </Bloco>
                </div>

                <div className="mt-3">
                  <Bloco titulo="Produção do mês" sub="o que a operação entregou" icone="fa-list-check">
                    <div className="row g-2">
                      {(dados.producao || []).map((p) => (
                        <div key={p.chave} className="col-md-4 col-xl-3">
                          <div className="border rounded p-2 h-100" style={{ borderLeft: `4px solid ${COR.navy}` }}>
                            <div className="small text-muted"><i className={`fas ${p.icone} me-1`}></i>{p.rotulo}</div>
                            <div className="fw-bold" style={{ fontSize: '1.4rem', fontVariantNumeric: 'tabular-nums' }}>{p.chave === 'horas' ? hm(p.valor) : num(p.valor)}</div>
                            <div className="small text-muted" style={{ lineHeight: 1.25 }}>{p.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Bloco>
                </div>
              </>
            )}

            {/* ═══════════ OPERADORAS ═══════════ */}
            {aba === 'operadoras' && (
              <div className="row g-3">
                <div className="col-xl-7">
                  <Bloco titulo="Operadoras" sub={`carteira e produção em ${dados.mes_rotulo}`} icone="fa-user-tie">
                    <Tabela chave={(l) => l.id} linhas={ops} ordemInicial={{ col: 'colaboradores', dir: 'desc' }}
                      colunas={[
                        { chave: 'nome', rotulo: 'Operadora', render: (l) => (<><div className="po-nome">{l.nome}</div><div className="po-sub">{l.clientes.join(', ') || 'sem empresa casada'}</div></>) },
                        { chave: 'n_clientes', rotulo: 'Empresas', classe: 'num' },
                        { chave: 'colaboradores', rotulo: 'Colab.', classe: 'num', render: (l) => num(l.colaboradores) },
                        { chave: 'admissoes', rotulo: 'Admissões', classe: 'num' },
                        { chave: 'rescisoes', rotulo: 'Rescisões', classe: 'num' },
                        { chave: 'ferias', rotulo: 'Férias', classe: 'num' },
                        { chave: 'avisos', rotulo: 'Avisos', classe: 'num', render: (l) => `${l.avisos}${l.contratos ? ` +${l.contratos} ctr` : ''}` },
                        { chave: 'beneficios', rotulo: 'VA/VT', classe: 'num' },
                        { chave: 'folhas', rotulo: 'Folhas', classe: 'num' },
                        { chave: 'horas', rotulo: 'Tempo (cronômetro)', classe: 'num', render: (l) => (l.horas ? hm(l.horas) : '—') },
                        { chave: 'tarefas_pct', rotulo: 'Ativ. mensais', classe: 'num', render: (l) => (l.tarefas ? `${l.tarefas_feitas}/${l.tarefas} (${pct(l.tarefas_pct)})` : '—') },
                        { chave: 'atrasos', rotulo: 'Atrasos', classe: 'num', render: (l) => (l.atrasos ? <span title={`${l.atrasos_internos} internos · ${l.atrasos_externos} externos`}><b className={l.atrasos_internos ? 'text-danger' : ''}>{l.atrasos_internos}</b> / {l.atrasos_externos}</span> : '—') },
                      ]} />
                    <div className="small text-muted mt-2">Atrasos = internos / externos justificados no mês. Horas = cronômetro por cliente (área RH). A empresa entra na conta da operadora pelo campo "operador" do cadastro (pode ter duas).</div>
                  </Bloco>
                </div>
                <div className="col-xl-5">
                  <Bloco titulo="Produção por operadora" sub="admissões, rescisões, férias e avisos do mês" icone="fa-chart-bar">
                    <ResponsiveContainer width="100%" height={Math.max(220, ops.length * 64 + 60)}>
                      <BarChart data={ops} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 0 }} barSize={22}>
                        <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="nome" width={90} tick={{ fontSize: 13, fill: '#212529', fontWeight: 600 }} axisLine={false} tickLine={false} />
                        <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                        <Legend content={<Legenda itens={[['Admissões', SERIES.admissoes], ['Rescisões', SERIES.rescisoes], ['Férias', SERIES.ferias], ['Avisos', SERIES.avisos]]} />} />
                        <Bar dataKey="admissoes" name="Admissões" stackId="a" fill={SERIES.admissoes} stroke="#fff" strokeWidth={2} />
                        <Bar dataKey="rescisoes" name="Rescisões" stackId="a" fill={SERIES.rescisoes} stroke="#fff" strokeWidth={2} />
                        <Bar dataKey="ferias" name="Férias" stackId="a" fill={SERIES.ferias} stroke="#fff" strokeWidth={2} />
                        <Bar dataKey="avisos" name="Avisos" stackId="a" fill={SERIES.avisos} stroke="#fff" strokeWidth={2} radius={[0, 8, 8, 0]}>
                          <LabelList dataKey="producao" position="right" offset={8} style={{ fontSize: 13, fontWeight: 700, fill: '#212529' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="small fw-bold text-muted mt-3 mb-1">Colaboradores por operadora</div>
                    <ResponsiveContainer width="100%" height={Math.max(120, ops.length * 40 + 30)}>
                      <BarChart data={ops} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 0 }} barSize={16}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="nome" width={90} tick={{ fontSize: 12, fill: '#495057' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                        <Bar dataKey="colaboradores" name="Colaboradores" fill={COR.navy} radius={[0, 8, 8, 0]}>
                          <LabelList dataKey="colaboradores" position="right" offset={8} style={{ fontSize: 12, fontWeight: 700, fill: '#212529' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Bloco>
                </div>
              </div>
            )}

            {/* ═══════════ CLIENTES ═══════════ */}
            {aba === 'clientes' && (() => {
              const evoTodas = dados.clientes_evolucao_todas || [];
              const evoCli = dados.clientes_evolucao || {};
              const sel = cliEvo !== 'todas' && evoCli[cliEvo] ? evoCli[cliEvo] : null;
              const serie = sel ? sel.serie : evoTodas;
              const PILHA = [['admissoes', 'Admissões', COR.azul], ['rescisoes', 'Rescisões', COR.vermelho], ['avisos', 'Avisos e contratos', COR.ambar],
                ['ferias', 'Férias', COR.verde], ['beneficios', 'VA · HO · VT pagos', '#6f42c1'], ['folha', 'Folha fechada', '#00b5cc']];
              const serieVol = serie.map((s) => ({ ...s, beneficios: (s.va || 0) + (s.ho || 0) + (s.vt || 0) }));
              const rotuloVal = { fontSize: 11.5, fontWeight: 700, fill: '#212529' };
              const subOps = sel?.operadoras?.length ? ` · operadora${sel.operadoras.length > 1 ? 's' : ''}: ${sel.operadoras.join(' e ')}` : '';
              // variação da carga entre o primeiro e o último mês da janela (e contra o mês anterior)
              const primeiroComCarga = serie.find((x) => x.carga);
              const ultimo = serie[serie.length - 1];
              const anterior = serie.length > 1 ? serie[serie.length - 2] : null;
              const varPct = (a, b) => (a && b && a.carga ? ((b.carga - a.carga) / a.carga) * 100 : null);
              const varJanela = varPct(primeiroComCarga, ultimo);
              const varMes = varPct(anterior, ultimo);
              const Variacao = ({ v, rotulo }) => {
                if (v == null) return null;
                const cor = v > 0.05 ? COR.verde : v < -0.05 ? COR.vermelho : COR.cinza;
                const icone = v > 0.05 ? 'fa-arrow-trend-up' : v < -0.05 ? 'fa-arrow-trend-down' : 'fa-minus';
                return (
                  <span className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1" title={rotulo}
                    style={{ background: `${cor}14`, color: cor, border: `1px solid ${cor}55`, fontSize: '.8rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    <i className={`fas ${icone}`}></i>{v > 0 ? '+' : ''}{num(v, 1)}%<span className="fw-normal" style={{ opacity: .8 }}> {rotulo}</span>
                  </span>
                );
              };
              return (
                <div className="mb-3">
                  <Bloco titulo="Evolução por empresa" icone="fa-chart-line"
                    sub={`${serie.length} mês(es) · ${sel ? sel.cliente : 'todas as empresas'}${subOps}`}
                    acao={(
                      <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 200 }} value={cliEvo} onChange={(e) => setCliEvo(e.target.value)} aria-label="Empresa do gráfico">
                        <option value="todas">Todas as empresas</option>
                        {cli.map((c) => <option key={c.cliente_id} value={String(c.cliente_id)}>{c.cliente}</option>)}
                      </select>
                    )}>
                    <div className="row g-3">
                      <div className="col-xl-6">
                        <div className="fw-bold text-muted mb-1" style={{ fontSize: '.95rem' }}><i className="fas fa-users me-1"></i>Colaboradores ativos no fim do mês</div>
                        <ResponsiveContainer width="100%" height={340}>
                          <AreaChart data={serie} margin={{ top: 18, right: 16, left: -8, bottom: 0 }}>
                            <defs><linearGradient id="gAtv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COR.navy} stopOpacity={0.25} /><stop offset="100%" stopColor={COR.navy} stopOpacity={0.02} /></linearGradient></defs>
                            <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                            <XAxis dataKey="rotulo" tick={{ fontSize: 13, fill: '#495057' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
                            <Tooltip content={<TooltipCard />} />
                            <Area type="monotone" dataKey="ativos" name="Ativos" stroke={COR.navy} fill="url(#gAtv)" strokeWidth={2.5} dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }}>
                              <LabelList dataKey="ativos" position="top" offset={8} style={rotuloVal} />
                            </Area>
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="col-xl-6">
                        <div className="fw-bold text-muted mb-1" style={{ fontSize: '.95rem' }}><i className="fas fa-right-left me-1"></i>Entradas × saídas de colaboradores</div>
                        <ResponsiveContainer width="100%" height={340}>
                          <BarChart data={serie} margin={{ top: 18, right: 8, left: -8, bottom: 0 }} barGap={3} barCategoryGap={24}>
                            <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                            <XAxis dataKey="rotulo" tick={{ fontSize: 13, fill: '#495057' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                            <Legend content={<Legenda itens={[['Entraram', COR.verde], ['Saíram', COR.vermelho]]} />} />
                            <Bar dataKey="admitidos" name="Entraram" fill={COR.verde} radius={[5, 5, 0, 0]}><LabelList dataKey="admitidos" position="top" formatter={(v) => (v ? v : '')} style={rotuloVal} /></Bar>
                            <Bar dataKey="desligados" name="Saíram" fill={COR.vermelho} radius={[5, 5, 0, 0]}><LabelList dataKey="desligados" position="top" formatter={(v) => (v ? v : '')} style={rotuloVal} /></Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="col-xl-6">
                        <div className="fw-bold text-muted mb-1" style={{ fontSize: '.95rem' }}><i className="fas fa-layer-group me-1"></i>Volume de trabalho (atividades no mês)</div>
                        <ResponsiveContainer width="100%" height={340}>
                          <BarChart data={serieVol} margin={{ top: 18, right: 8, left: -8, bottom: 0 }} barCategoryGap={22}>
                            <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                            <XAxis dataKey="rotulo" tick={{ fontSize: 13, fill: '#495057' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                            <Legend content={<Legenda itens={PILHA.map(([, rot, cor]) => [rot, cor])} />} />
                            {PILHA.map(([ch, rot, cor], i) => (
                              <Bar key={ch} dataKey={ch} name={rot} stackId="v" fill={cor} stroke="#fff" strokeWidth={2} radius={i === PILHA.length - 1 ? [5, 5, 0, 0] : 0}>
                                {i === PILHA.length - 1 && <LabelList dataKey="atividades" position="top" formatter={(v) => (v ? v : '')} style={rotuloVal} />}
                              </Bar>
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="col-xl-6">
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                          <div className="fw-bold text-muted" style={{ fontSize: '.95rem' }}>
                            <i className="fas fa-cubes-stacked me-1"></i>Carga de serviços (serviços × colaboradores)
                          </div>
                          <Variacao v={varJanela} rotulo={primeiroComCarga && ultimo ? `de ${primeiroComCarga.rotulo} a ${ultimo.rotulo}` : ''} />
                          <Variacao v={varMes} rotulo="no último mês" />
                        </div>
                        <div className="text-muted small mb-1">
                          {sel
                            ? (sel.servicos?.length ? `${sel.servicos.length} serviço(s): ${sel.servicos.join(', ')}` : 'sem serviço por colaborador cadastrado')
                            : 'soma de cada empresa'}
                          {ultimo?.carga ? ` · ${num(ultimo.carga)} em ${ultimo.rotulo}` : ''}
                        </div>
                        <ResponsiveContainer width="100%" height={340}>
                          <BarChart data={serie} margin={{ top: 18, right: 8, left: -8, bottom: 0 }} barCategoryGap={28}>
                            <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                            <XAxis dataKey="rotulo" tick={{ fontSize: 13, fill: '#495057' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                            <Bar dataKey="carga" name="Serviços × colaboradores" fill={COR.navy} radius={[6, 6, 0, 0]}>
                              <LabelList dataKey="carga" position="top" formatter={(v) => (v ? num(v) : '')} style={rotuloVal} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="small text-muted mt-2">
                      <b>Como ler:</b> quando saem mais colaboradores do que entram, o headcount cai e o volume de VA, VT, home office e ponto tende a cair junto;
                      admissões e rescisões concentram trabalho no mês em que acontecem. <b>Volume</b> = admissões + rescisões + férias iniciadas + avisos/contratos
                      enviados + pagamentos de VA/HO/VT lançados + folha fechada. <b>Carga de serviços</b> = quantos serviços por colaborador a empresa
                      contratou (VA, VT, home office, ponto, plano de saúde/odonto, medicina do trabalho, academia e folha, pelo cadastro de Serviços)
                      × colaboradores ativos no fim do mês — se o headcount cai, a carga cai junto; em "Todas as empresas" é a soma de cada uma.
                      As operadoras da empresa vêm do cadastro (não há histórico de troca por mês).
                    </div>
                  </Bloco>
                </div>
              );
            })()}
            {aba === 'clientes' && (
              <Bloco titulo="Clientes" sub={`uma linha por empresa · ${dados.mes_rotulo} · clique no cabeçalho para ordenar`} icone="fa-building" count={cli.length}>
                <Tabela chave={(l) => l.cliente_id} linhas={cli} ordemInicial={{ col: 'colaboradores', dir: 'desc' }}
                  colunas={[
                    { chave: 'cliente', rotulo: 'Empresa', render: (l) => (<><Link to={`/BpoRh/Folha/cliente/${l.cliente_id}?mes=${dados.mes}`} className="po-nome text-decoration-none">{l.cliente}</Link><div className="po-sub">{l.operador || '—'}</div></>) },
                    { chave: 'colaboradores', rotulo: 'Colab.', classe: 'num', render: (l) => num(l.colaboradores) },
                    { chave: 'admitidos', rotulo: 'Entradas', classe: 'num', render: (l) => (l.admitidos ? <span style={{ color: COR.verde }}>+{l.admitidos}</span> : '—') },
                    { chave: 'desligados', rotulo: 'Saídas', classe: 'num', render: (l) => (l.desligados ? <span style={{ color: COR.vermelho }}>−{l.desligados}</span> : '—') },
                    { chave: 'turnover_pct', rotulo: 'Turnover', classe: 'num', render: (l) => <span className={l.turnover_pct > 5 ? 'text-danger fw-bold' : ''}>{pct(l.turnover_pct)}</span> },
                    { chave: 'admissoes', rotulo: 'Admissões', classe: 'num' },
                    { chave: 'rescisoes', rotulo: 'Rescisões', classe: 'num' },
                    { chave: 'ferias', rotulo: 'Férias', classe: 'num' },
                    { chave: 'avisos', rotulo: 'Avisos', classe: 'num' },
                    { chave: 'beneficios', rotulo: 'VA/VT', classe: 'num' },
                    { chave: 'mensagens', rotulo: 'Mensagens', classe: 'num', render: (l) => (l.mensagens ? <span title={`${l.mensagens_cliente} do cliente · ${l.mensagens_equipe} da equipe`}>{num(l.mensagens)}</span> : '—') },
                    { chave: 'horas', rotulo: 'Tempo (cronômetro)', classe: 'num', render: (l) => (l.horas ? <span title={l.horas_por_colaborador != null ? `${hm(l.horas_por_colaborador)} por colaborador` : ''}>{hm(l.horas)}</span> : '—') },
                    { chave: 'atrasos', rotulo: 'Atrasos', classe: 'num', render: (l) => (l.atrasos ? <span className={l.atrasos - l.atrasos_externos ? 'text-danger fw-bold' : ''}>{l.atrasos}{l.atrasos_externos ? <span className="text-muted fw-normal"> ({l.atrasos_externos} ext.)</span> : ''}</span> : '—') },
                    { chave: 'folha_fechada', rotulo: 'Folha', valor: (l) => (l.folha_fechada ? 2 : (l.folha_prazo ? 1 : 0)),
                      render: (l) => (l.folha_fechada ? <span className="badge bg-success"><i className="fas fa-lock me-1"></i>fechada{l.folha_enviada_dp ? ' · DP' : ''}</span>
                        : (l.folha_prazo ? <span className="badge bg-warning text-dark" title={`prazo: ${l.folha_prazo}`}>aberta</span> : <span className="text-muted">—</span>)) },
                  ]} />
                {(dados.ocultos || []).length > 0 && <div className="small text-muted mt-2">Fora da conta (sem folha por aqui): {dados.ocultos.join(', ')}.</div>}
                {/* legenda: o que cada coluna significa e de onde vem */}
                <div className="border rounded p-3 mt-3" style={{ background: '#f8f9fa' }}>
                  <div className="fw-bold mb-2"><i className="fas fa-circle-info me-2 text-muted"></i>Como ler esta tabela</div>
                  <div className="row g-2 small">
                    {[
                      ['Colab.', 'colaboradores ativos hoje na Convenia (todos os vínculos).'],
                      ['Entradas / Saídas', 'quem foi admitido e quem foi desligado no mês, pela data da Convenia.'],
                      ['Turnover', 'saídas do mês ÷ colaboradores ativos. Fica vermelho acima de 5%.'],
                      ['Admissões / Rescisões', 'processos que a equipe tocou no mês (checklists de admissão e rescisão), pela data de entrada/desligamento.'],
                      ['Férias', 'férias que começaram no mês (solicitações da Convenia não canceladas).'],
                      ['Avisos', 'e-mails enviados pelo Saber no mês: aviso de ponto, fim de experiência, férias, exame, mais os contratos anexados.'],
                      ['VA/VT', 'pagamentos de VA, home office e VT lançados no mês nos módulos de benefícios.'],
                      ['Mensagens', 'mensagens do Slack espelhado no mês (passe o mouse: quantas do cliente e quantas da equipe). Só empresas com canal conectado.'],
                      ['Tempo (cronômetro)', 'tempo do cronômetro que a operadora liga ao abrir a empresa (área RH), em horas e minutos (19h54min). Passe o mouse para ver o tempo por colaborador.'],
                      ['Atrasos', 'justificativas registradas no modal de atrasos no mês; em vermelho quando há causa interna (nossa). "ext." = causa do cliente/terceiro.'],
                      ['Folha', 'situação da folha da competência: fechada (e se já foi ao DP), aberta com prazo cadastrado, ou "—" sem prazo.'],
                    ].map(([rot, txt]) => (
                      <div key={rot} className="col-md-6 col-xl-4 d-flex gap-2">
                        <span className="badge bg-light text-dark border" style={{ minWidth: 90, height: 'fit-content' }}>{rot}</span>
                        <span className="text-muted">{txt}</span>
                      </div>
                    ))}
                  </div>
                  <div className="small text-muted mt-2">
                    Leitura rápida: turnover alto + muitas mensagens + poucas horas apontadas costuma indicar empresa que demanda mais do que registramos;
                    atrasos internos com folha aberta perto do prazo pedem atenção do gestor.
                  </div>
                </div>
              </Bloco>
            )}

            {/* ═══════════ ATRASOS ═══════════ */}
            {aba === 'atrasos' && (
              <div className="row g-3">
                <div className="col-xl-4">
                  <Bloco titulo="Origem dos atrasos" sub={`justificados em ${dados.mes_rotulo}`} icone="fa-diagram-project">
                    {atr.total ? (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={[{ name: 'Interno (nosso)', value: atr.por_origem.INTERNO || 0 }, { name: 'Externo (cliente/terceiro)', value: atr.por_origem.EXTERNO || 0 }]}
                              dataKey="value" innerRadius={62} outerRadius={95} paddingAngle={3} stroke="#fff" strokeWidth={2}>
                              <Cell fill={COR.vermelho} /><Cell fill={COR.ambar} />
                            </Pie>
                            <Tooltip content={<TooltipCard />} />
                          </PieChart>
                        </ResponsiveContainer>
                        <Legenda itens={[[`Interno · ${atr.por_origem.INTERNO || 0}`, COR.vermelho], [`Externo · ${atr.por_origem.EXTERNO || 0}`, COR.ambar]]} />
                        <div className="text-center small text-muted mt-2">{atr.total} atraso(s) · média de {atr.dias_medio ?? '—'} dia(s) quando justificado</div>
                      </>
                    ) : <div className="po-vazio text-center text-muted py-4">Nenhum atraso justificado neste mês.</div>}
                    {atr.por_tipo.length > 0 && (
                      <>
                        <div className="small fw-bold text-muted mt-3 mb-1">Por atividade</div>
                        <ResponsiveContainer width="100%" height={Math.max(120, atr.por_tipo.length * 38 + 20)}>
                          <BarChart data={atr.por_tipo} layout="vertical" margin={{ top: 4, right: 36, left: 8, bottom: 0 }} barSize={14}>
                            <XAxis type="number" hide allowDecimals={false} />
                            <YAxis type="category" dataKey="rotulo" width={150} tick={{ fontSize: 11.5, fill: '#495057' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                            <Bar dataKey="INTERNO" name="Interno" stackId="a" fill={COR.vermelho} stroke="#fff" strokeWidth={2} />
                            <Bar dataKey="EXTERNO" name="Externo" stackId="a" fill={COR.ambar} stroke="#fff" strokeWidth={2} radius={[0, 6, 6, 0]}>
                              <LabelList dataKey="total" position="right" offset={6} style={{ fontSize: 12, fontWeight: 700, fill: '#212529' }} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </>
                    )}
                    {(() => {
                      // todas as operadoras entram (quem está zerada aparece com barra vazia)
                      const porOp = ops.map((o) => {
                        const a = atr.por_operador.find((x) => x.operador === o.nome) || {};
                        return { operador: o.nome, INTERNO: a.INTERNO || 0, EXTERNO: a.EXTERNO || 0, total: a.total || 0,
                          colaboradores: o.colaboradores || 0 };
                      }).sort((a, b) => b.total - a.total);
                      return (
                        <>
                          <div className="small fw-bold text-muted mt-3 mb-1"><i className="fas fa-user-tie me-1"></i>Atrasos por operadora</div>
                          <ResponsiveContainer width="100%" height={Math.max(130, porOp.length * 44 + 30)}>
                            <BarChart data={porOp} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 0 }} barSize={18}>
                              <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                              <YAxis type="category" dataKey="operador" width={90} tick={{ fontSize: 12.5, fill: '#212529', fontWeight: 600 }} axisLine={false} tickLine={false} />
                              <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                              <Bar dataKey="INTERNO" name="Interno" stackId="o" fill={COR.vermelho} stroke="#fff" strokeWidth={2} />
                              <Bar dataKey="EXTERNO" name="Externo" stackId="o" fill={COR.ambar} stroke="#fff" strokeWidth={2} radius={[0, 6, 6, 0]}>
                                <LabelList dataKey="total" position="right" offset={6} formatter={(v) => (v ? v : '0')} style={{ fontSize: 12, fontWeight: 700, fill: '#212529' }} />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                          <Legenda itens={[['Interno (nosso)', COR.vermelho], ['Externo (cliente/terceiro)', COR.ambar]]} />
                          <div className="small text-muted mt-1">
                            {porOp.filter((o) => o.colaboradores).map((o) => `${o.operador}: ${o.total} atraso(s) para ${num(o.colaboradores)} colaboradores`).join(' · ')}
                          </div>
                        </>
                      );
                    })()}
                  </Bloco>
                </div>
                <div className="col-xl-8">
                  <Bloco titulo="Justificativas do mês" sub="o que cada operadora respondeu no modal de atrasos" icone="fa-comment-dots" count={atr.lista.length}>
                    <Tabela chave={(l, i) => `${l.tipo}-${l.cliente}-${l.colaborador}-${l.referencia}`} linhas={atr.lista} ordemInicial={{ col: 'em', dir: 'desc' }}
                      vazio="Nenhuma justificativa neste mês."
                      colunas={[
                        { chave: 'em', rotulo: 'Quando', estilo: { width: 120 }, render: (l) => <span className="small">{l.em}</span> },
                        { chave: 'tipo_rotulo', rotulo: 'Atividade', render: (l) => <span className="badge bg-light text-dark border">{l.tipo_rotulo}</span> },
                        { chave: 'colaborador', rotulo: 'Colaborador / empresa', render: (l) => (<><div className="po-nome">{l.colaborador}</div><div className="po-sub">{l.cliente}{l.referencia ? ` · ref. ${l.referencia}` : ''}</div></>) },
                        { chave: 'dias_atraso', rotulo: 'Dias', classe: 'num', render: (l) => <span className="badge bg-danger">{l.dias_atraso}</span> },
                        { chave: 'origem', rotulo: 'Origem', render: (l) => <span className={`badge ${l.origem === 'INTERNO' ? 'bg-danger' : 'bg-warning text-dark'}`}>{l.origem}</span> },
                        { chave: 'observacao', rotulo: 'Justificativa', render: (l) => (<><div>{l.observacao}</div>{l.onde && <div className="po-sub">estava em: {l.onde}</div>}</>) },
                        { chave: 'operador', rotulo: 'Quem', render: (l) => <span className="small">{l.operador}</span> },
                      ]} />
                  </Bloco>
                </div>
              </div>
            )}

            {/* ═══════════ ATENDIMENTO E TEMPO ═══════════ */}
            {aba === 'atendimento' && (
              <div className="row g-3">
                <div className="col-xl-6">
                  <Bloco titulo="Atendimento (Slack)" sub={`mensagens em ${dados.mes_rotulo}, por empresa`} icone="fa-comments">
                    {dados.atendimento?.por_cliente?.length ? (
                      <>
                        <ResponsiveContainer width="100%" height={Math.max(140, dados.atendimento.por_cliente.length * 44 + 40)}>
                          <BarChart data={dados.atendimento.por_cliente} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 0 }} barSize={18}>
                            <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                            <XAxis type="number" tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                            <YAxis type="category" dataKey="cliente" width={110} tick={{ fontSize: 12.5, fill: '#212529', fontWeight: 600 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                            <Legend content={<Legenda itens={[['Do cliente', COR.azul], ['Da equipe', COR.verde]]} />} />
                            <Bar dataKey="cliente_msgs" name="Do cliente" stackId="a" fill={COR.azul} stroke="#fff" strokeWidth={2} />
                            <Bar dataKey="equipe_msgs" name="Da equipe" stackId="a" fill={COR.verde} stroke="#fff" strokeWidth={2} radius={[0, 8, 8, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="small fw-bold text-muted mt-3 mb-1">Quem mais responde</div>
                        {(dados.atendimento.por_autor || []).map((a) => (
                          <div key={a.autor} className="d-flex justify-content-between small border-bottom py-1"><span>{a.autor}</span><b style={{ fontVariantNumeric: 'tabular-nums' }}>{num(a.mensagens)}</b></div>
                        ))}
                      </>
                    ) : <div className="po-vazio text-center text-muted py-4">Sem conversas espelhadas neste mês (só empresas com canal Slack/Discord conectado entram aqui).</div>}
                  </Bloco>
                </div>
                <div className="col-xl-6">
                  <Bloco titulo="Tempo dedicado por empresa" sub="cronômetro por cliente (área RH), em horas" icone="fa-stopwatch">
                    {dados.tempo_por_empresa?.length ? (
                      <ResponsiveContainer width="100%" height={Math.max(140, dados.tempo_por_empresa.length * 36 + 40)}>
                        <BarChart data={dados.tempo_por_empresa} layout="vertical" margin={{ top: 4, right: 52, left: 8, bottom: 0 }} barSize={16}>
                          <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                          <XAxis type="number" tick={{ fontSize: 12, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="empresa" width={130} tick={{ fontSize: 12.5, fill: '#212529', fontWeight: 600 }} axisLine={false} tickLine={false} />
                          <Tooltip content={<TooltipCard unidade="h" />} cursor={{ fill: 'rgba(0,32,91,.05)' }} />
                          <Bar dataKey="horas" name="Horas" fill={COR.ambar} radius={[0, 8, 8, 0]}>
                            <LabelList dataKey="horas" position="right" offset={8} formatter={(v) => hm(v)} style={{ fontSize: 12, fontWeight: 700, fill: '#212529' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div className="po-vazio text-center text-muted py-4">Nenhum tempo apontado na área RH neste mês.</div>}
                    <div className="small text-muted mt-2">O tempo vem do cronômetro que a operadora liga ao abrir a empresa — só conta o que foi registrado.</div>
                  </Bloco>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GestaoBpoRh;
